import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { connect } from "react-redux";
// @material-ui/core components
import { Card, CardContent, CardHeader, useMediaQuery } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import FormGroup from "@material-ui/core/FormGroup";
import FormLabel from "@material-ui/core/FormLabel";
import Grid from "@material-ui/core/Grid";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import { makeStyles, useTheme } from "@material-ui/core/styles";
// components
import GenericButton from "components/Buttons/GenericButton";
import FormSelect from "components/FormFields/FormSelect";
//import custom components
import componentStylesGeneric from "assets/theme/views/admin/generic.js";
import useFilterSync from "hooks/useFilterSync";
import useQueryParams from "hooks/useQueryParams";
import { getDataSetByNameAction } from "redux/actions/datasetsAction";
import { getFilteredInformationAction } from "redux/actions/informationDisseminationAction";
import { DATASET, INFORMATION_DISSEMINATION_TYPE_KEYVALUE, PAGINATION } from "utils/const";
import { getSelectOptionsElementByKeyValue } from "utils/formSelect";

const useStylesGeneric = makeStyles(componentStylesGeneric);

function FilterSection(props) {
  const classes = useStylesGeneric();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const submittedFilterRef = useRef(null);
  const [informationDisseminationTypes, setInformationDisseminationTypes] = useState([]);
  const disseminationTypeDataset = props.datasetsState?.[DATASET.INFORMATION_DISSEMINATION_TYPE];
  const { getQueryParam } = useQueryParams();

  const postInformationTypeKeyValuesList = useMemo(
    () => [
      INFORMATION_DISSEMINATION_TYPE_KEYVALUE.NEWS,
      INFORMATION_DISSEMINATION_TYPE_KEYVALUE.LEGISLATION,
      INFORMATION_DISSEMINATION_TYPE_KEYVALUE.SCHEDULE,
    ],
    []
  );

  const {
    handleSubmit,
    getValues,
    setValue,
    reset,
    watch,
    control,
    formState: { errors, isValid },
  } = useFormContext();

  const pageIndex = watch("pageIndex") ?? PAGINATION.FIRST_PAGE;
  const pageSize = watch("pageSize") ?? PAGINATION.PAGE_SIZE_DISSEMINATION;

  const { handleFilterSubmit, handleFilterReset, updatePagination } = useFilterSync({
    defaultValues: {
      searchText: "",
      informationDisseminationTypeKeyValue: null,
      searchPublishPeriodKeyValue: null,
      pageIndex: PAGINATION.FIRST_PAGE,
      pageSize: PAGINATION.PAGE_SIZE_DISSEMINATION,
    },
    syncOnMount: true,
    syncPagination: true,
    pagination: {
      pageIndexKey: "pageIndex",
      pageSizeKey: "pageSize",
      defaultPageSize: PAGINATION.PAGE_SIZE_DISSEMINATION,
    },
    fieldSerializers: {
      informationDisseminationTypeKeyValue: {
        toUrl: (value) => (value && typeof value === "object" ? value.keyValue : value),
        fromUrl: (value) => value,
      },
      searchPublishPeriodKeyValue: {
        toUrl: (value) => (value && typeof value === "object" ? value.keyValue : value),
        fromUrl: (value) => value,
      },
    },
  });

  // Restore select objects when options lists are ready
  useEffect(() => {
    const currentType = getValues("informationDisseminationTypeKeyValue");
    if (currentType && typeof currentType !== "object" && informationDisseminationTypes?.length > 0) {
      const typeOption = getSelectOptionsElementByKeyValue(informationDisseminationTypes, currentType);
      if (typeOption) setValue("informationDisseminationTypeKeyValue", typeOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [informationDisseminationTypes]);

  useEffect(() => {
    const currentPeriod = getValues("searchPublishPeriodKeyValue");
    const periodOptions = DATASET.infomrationDisseminationPublishPeriodDropDown;
    if (currentPeriod && typeof currentPeriod !== "object" && Array.isArray(periodOptions)) {
      const periodOption = getSelectOptionsElementByKeyValue(periodOptions, currentPeriod);
      if (periodOption) setValue("searchPublishPeriodKeyValue", periodOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildPayload = (values, override) => {
    const typeValue = values.informationDisseminationTypeKeyValue;
    const typeKeyValue = typeValue && typeof typeValue === "object" ? typeValue.keyValue : typeValue;

    const periodValue = values.searchPublishPeriodKeyValue;
    const periodKeyValue = periodValue && typeof periodValue === "object" ? periodValue.keyValue : periodValue;

    const resolvedTypeKeyValue = props.informationDisseminationType ?? typeKeyValue;

    return {
      searchText: values.searchText ?? "",
      searchPublishPeriodKeyValue: periodKeyValue,
      informationDisseminationTypeKeyValueList: resolvedTypeKeyValue
        ? [resolvedTypeKeyValue]
        : postInformationTypeKeyValuesList,
      isPublic: props.authState.loggedIn !== undefined && props.authState.loggedIn ? null : true,
      published: true,
      isHighlighted: null,
      pageIndex: override?.pageIndex ?? pageIndex,
      pageSize: override?.pageSize ?? pageSize,
    };
  };

  const cleanFilter = () => {
    const defaultTypeOption = getSelectOptionsElementByKeyValue(
      informationDisseminationTypes,
      props.informationDisseminationType
    );

    const nextFormValues = {
      searchText: "",
      informationDisseminationTypeKeyValue: defaultTypeOption ?? null,
      searchPublishPeriodKeyValue: null,
      pageIndex: PAGINATION.FIRST_PAGE,
      pageSize: PAGINATION.PAGE_SIZE_DISSEMINATION,
    };

    reset(nextFormValues);

    handleFilterReset();

    const next = buildPayload(nextFormValues, {
      pageIndex: PAGINATION.FIRST_PAGE,
      pageSize: PAGINATION.PAGE_SIZE_DISSEMINATION,
    });

    submittedFilterRef.current = next;
    props.getFilterInformation(next);
  };

  const onSubmit = (data) => {
    if (isValid) {
      const nextFormValues = {
        ...data,
        pageIndex: PAGINATION.FIRST_PAGE,
        pageSize: data.pageSize ?? PAGINATION.PAGE_SIZE_DISSEMINATION,
      };

      // Submeter sempre volta para a 1ª página (form + URL)
      if ((data.pageIndex ?? PAGINATION.FIRST_PAGE) !== PAGINATION.FIRST_PAGE) {
        setValue("pageIndex", PAGINATION.FIRST_PAGE);
      }

      handleFilterSubmit(nextFormValues);

      const next = buildPayload(nextFormValues, {
        pageIndex: PAGINATION.FIRST_PAGE,
        pageSize: nextFormValues.pageSize,
      });

      submittedFilterRef.current = next;

      if ((data.pageIndex ?? PAGINATION.FIRST_PAGE) === PAGINATION.FIRST_PAGE) {
        props.getFilterInformation(next);
      }
    }
  };

  // Keep Categoria select in sync with route param (without wiping other filters)
  useEffect(() => {
    if (!props.informationDisseminationType) return;
    if (!informationDisseminationTypes?.length) return;

    const defaultTypeOption = getSelectOptionsElementByKeyValue(
      informationDisseminationTypes,
      props.informationDisseminationType
    );

    if (defaultTypeOption) {
      setValue("informationDisseminationTypeKeyValue", defaultTypeOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.informationDisseminationType, informationDisseminationTypes]);

  useEffect(() => {
    if (!submittedFilterRef.current) {
      const values = getValues();
      submittedFilterRef.current = buildPayload(values);
    } else {
      submittedFilterRef.current = {
        ...submittedFilterRef.current,
        pageIndex,
        pageSize,
      };
    }

    updatePagination(pageIndex, pageSize);

    props.getFilterInformation(submittedFilterRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize]);

  useEffect(() => {
    if (
      props.datasetsState[DATASET.INFORMATION_DISSEMINATION_TYPE] === undefined ||
      props.datasetsState[DATASET.INFORMATION_DISSEMINATION_TYPE]?.length === 0
    ) {
      props.getDataSetByName(DATASET.INFORMATION_DISSEMINATION_TYPE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setInformationDisseminationTypes(
      filterListByExcludingKeyValue(
        disseminationTypeDataset,
        INFORMATION_DISSEMINATION_TYPE_KEYVALUE.STRUCTURING_PROJECT
      )
    );
  }, [disseminationTypeDataset]);

  // If user lands with filters in URL, trigger initial search once
  useEffect(() => {
    const hasFilters =
      (getQueryParam("searchText") ?? "") !== "" ||
      getQueryParam("informationDisseminationTypeKeyValue") !== undefined ||
      getQueryParam("searchPublishPeriodKeyValue") !== undefined;

    if (hasFilters && !submittedFilterRef.current) {
      handleSubmit(onSubmit)();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterListByExcludingKeyValue = (list, keyValue) => {
    return list?.filter((item) => item.keyValue !== keyValue);
  };

  return (
    <Card classes={{ root: classes.cardRoot }}>
      <CardHeader
        classes={{
          action: classes.cardHeaderAction,
          title: classes.cardHeader,
        }}
        title={props.title}
      ></CardHeader>
      <CardContent>
        <Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container>
              <Grid item xs={12} sm={12} md={4}>
                <FormGroup>
                  <FormLabel>Pesquisa (título/descrição)</FormLabel>
                  <Controller
                    name="searchText"
                    control={control}
                    defaultValue=""
                    rules={{ required: { value: false } }}
                    render={({ field: { onChange, name, value } }) => (
                      <OutlinedInput
                        name={name}
                        value={value}
                        onChange={(e) => {
                          onChange(e.target.value);
                        }}
                        fullWidth
                        autoComplete="off"
                        type="text"
                        placeholder="Pesquisar"
                        classes={{
                          notchedOutline: clsx({
                            [classes.borderWarning]: errors["searchText"] !== undefined,
                          }),
                        }}
                      />
                    )}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={12} md={3}>
                <FormGroup>
                  <FormLabel>Categoria</FormLabel>
                  <FormSelect
                    control={control}
                    fieldName="informationDisseminationTypeKeyValue"
                    selectOptions={informationDisseminationTypes}
                    getValues={getValues}
                    setValue={setValue}
                    errors={errors}
                    classes={classes}
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={12} sm={12} md={3}>
                <FormGroup>
                  <FormLabel>Data Publicação</FormLabel>
                  <FormSelect
                    control={control}
                    fieldName="searchPublishPeriodKeyValue"
                    selectOptions={DATASET.infomrationDisseminationPublishPeriodDropDown}
                    getValues={getValues}
                    setValue={setValue}
                    errors={errors}
                    classes={classes}
                  />
                </FormGroup>
              </Grid>

              {/* Botões de Pesquisa */}

              <Grid item xs={12} md={12} classes={{ root: classes.textAlignRight }}>
                <Box
                  display="flex"
                  justifyContent={isSmDown ? "stretch" : "flex-end"}
                  alignItems={isSmDown ? "stretch" : "center"}
                  flexDirection={isSmDown ? "column" : "row"}
                  gridGap={12}
                >
                  <GenericButton
                    typeSubmit={false}
                    color="ghost"
                    onClick={() => {
                      cleanFilter();
                    }}
                    fullWidth={isSmDown}
                    size={isSmDown ? "medium" : undefined}
                  >
                    Limpar Filtros
                  </GenericButton>
                  <GenericButton
                    typeSubmit={true}
                    color="primary"
                    loading={props.informationDisseminationState.filteredInformation_loading}
                    fullWidth={isSmDown}
                    size={isSmDown ? "medium" : undefined}
                  >
                    Pesquisar
                  </GenericButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </CardContent>
    </Card>
  );
}

const mapStateToProps = (state) => ({ ...state });

const mapDispatchToProps = (dispatch) => ({
  getFilterInformation: (data) => dispatch(getFilteredInformationAction(data)),
  getDataSetByName: (name) => dispatch(getDataSetByNameAction(name)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FilterSection);
